/**
 * useGuestsState - Custom hook for Guests module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialGuestForm = {
  name: '',
  role: '',
  company: '',
  bio: '',
  image: '',
  socialLinks: {},
  isFeatured: false,
  isNew: true
};

const initialInlineGuestForm = {
  name: '',
  role: '',
  company: '',
  bio: '',
  image: '',
  isFeatured: false
};

export const useGuestsState = ({ setError, setSuccess }) => {
  // Core guests state
  const [cmsGuests, setCmsGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestModalView, setGuestModalView] = useState('edit'); // 'edit' or 'media'

  // Inline guest creation state (for Episode Editor)
  const [guestSearchQuery, setGuestSearchQuery] = useState('');
  const [newGuestData, setNewGuestData] = useState(initialInlineGuestForm);

  // Fetch all guests (admin view — includes hidden)
  const fetchCmsGuests = useCallback(async () => {
    setLoadingGuests(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/guests?include_hidden=true`);
      setCmsGuests(response.data || []);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Failed to load guests');
    } finally {
      setLoadingGuests(false);
    }
  }, [setError]);

  // Create new guest
  const handleCreateGuest = useCallback(() => {
    setEditingGuest({ ...initialGuestForm });
    setShowGuestModal(true);
  }, []);

  // Edit existing guest
  const handleEditGuest = useCallback((guest) => {
    setEditingGuest({ ...guest });
    setShowGuestModal(true);
  }, []);

  // Save guest (create or update)
  const handleSaveGuest = useCallback(async () => {
    if (!editingGuest) return;

    if (!editingGuest.name?.trim()) {
      setError('Guest name is required');
      return;
    }

    try {
      if (editingGuest.isNew) {
        const { isNew, ...guestData } = editingGuest;
        await axios.post(`${API_URL}/api/cms/guests`, guestData, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingGuest.name}" created successfully!`);
      } else {
        await axios.put(`${API_URL}/api/cms/guests/${editingGuest.id}`, editingGuest, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingGuest.name}" saved successfully!`);
      }

      setShowGuestModal(false);
      setEditingGuest(null);
      fetchCmsGuests();
    } catch (err) {
      setError('Failed to save guest: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingGuest, fetchCmsGuests, setError, setSuccess]);

  // Delete guest
  const handleDeleteGuest = useCallback(async (guestId, guestName) => {
    if (!window.confirm(`Are you sure you want to delete "${guestName}"? This will also remove them from any episodes.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/cms/guests/${guestId}`, {
        headers: getAuthHeaders()
      });
      setSuccess(`"${guestName}" deleted successfully`);
      fetchCmsGuests();
    } catch (err) {
      setError('Failed to delete guest: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsGuests, setError, setSuccess]);

  // Toggle hide/show on live site
  const handleToggleHiddenGuest = useCallback(async (guestId, guestName) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/cms/guests/${guestId}/toggle-hidden`,
        {},
        { headers: getAuthHeaders() },
      );
      const isHidden = !!response.data?.isHidden;
      // Optimistic local update so the UI reflects the change instantly
      setCmsGuests(prev =>
        prev.map(g => (g.id === guestId ? { ...g, isHidden } : g))
      );
      setSuccess(
        isHidden
          ? `"${guestName}" is now hidden from the live site`
          : `"${guestName}" is now visible on the live site`
      );
    } catch (err) {
      setError('Failed to toggle guest visibility: ' + (err.response?.data?.detail || err.message));
    }
  }, [setError, setSuccess]);

  // Handle field changes in edit form
  const handleGuestFieldChange = useCallback((field, value) => {
    setEditingGuest(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  // Inline guest handlers (for nested creation in Episode Editor)
  const handleOpenInlineAddGuest = useCallback((setEpisodeModalMode) => {
    setNewGuestData({ ...initialInlineGuestForm });
    setEpisodeModalMode('addGuest');
  }, []);

  const handleBackFromAddGuest = useCallback((setEpisodeModalMode) => {
    setEpisodeModalMode('edit');
  }, []);

  const handleNestedGuestFieldChange = useCallback((field, value) => {
    setNewGuestData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveInlineGuest = useCallback(async (editingEpisode, handleEpisodeFieldChange, setEpisodeModalMode) => {
    if (!newGuestData.name?.trim()) {
      setError('Guest name is required');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/cms/guests`, newGuestData, {
        headers: getAuthHeaders()
      });

      const createdGuest = response.data;

      // Add new guest to the list
      setCmsGuests(prev => [...prev, createdGuest]);

      // Auto-tag the new guest to the current episode
      if (editingEpisode && handleEpisodeFieldChange) {
        const currentGuests = editingEpisode.guests || [];
        handleEpisodeFieldChange('guests', [...currentGuests, createdGuest.id]);
      }

      setSuccess(`"${newGuestData.name}" created and tagged to episode!`);

      // Return to episode edit view
      setEpisodeModalMode('edit');

      // Reset form
      setNewGuestData({ ...initialInlineGuestForm });
    } catch (err) {
      setError('Failed to create guest: ' + (err.response?.data?.detail || err.message));
    }
  }, [newGuestData, setError, setSuccess]);

  return {
    // State
    cmsGuests,
    loadingGuests,
    editingGuest,
    showGuestModal,
    guestModalView,
    guestSearchQuery,
    newGuestData,

    // Setters
    setCmsGuests,
    setEditingGuest,
    setShowGuestModal,
    setGuestModalView,
    setGuestSearchQuery,
    setNewGuestData,

    // Actions
    fetchCmsGuests,
    handleCreateGuest,
    handleEditGuest,
    handleSaveGuest,
    handleDeleteGuest,
    handleToggleHiddenGuest,
    handleGuestFieldChange,
    handleOpenInlineAddGuest,
    handleBackFromAddGuest,
    handleNestedGuestFieldChange,
    handleSaveInlineGuest,
  };
};

export default useGuestsState;
